import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

const primitiveStyles = {
  string: 'text-success-700',
  number: 'text-info-700',
  boolean: 'text-warning-700',
  empty: 'text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor',
};

const isBranch = (value) => value !== null && typeof value === 'object';

const toEntries = (value) =>
  Array.isArray(value) ? value.map((item, index) => [String(index), item]) : Object.entries(value);

const Primitive = ({ value }) => {
  if (value === null || value === undefined) return <span className={primitiveStyles.empty}>null</span>;
  if (typeof value === 'string') return <span className={primitiveStyles.string}>"{value}"</span>;
  return <span className={primitiveStyles[typeof value] ?? primitiveStyles.empty}>{String(value)}</span>;
};

const Node = ({ label, value, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  if (!isBranch(value)) {
    return (
      <div className="flex gap-2 py-0.5">
        <span className="shrink-0 text-primary-600 dark:text-primary-300">{label}:</span>
        <Primitive value={value} />
      </div>
    );
  }

  const entries = toEntries(value);
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div className="py-0.5">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex items-center gap-1 text-primary-600 dark:text-primary-300"
      >
        <Chevron className="h-3.5 w-3.5" aria-hidden="true" />
        <span>{label}</span>
        <span className="text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
          {Array.isArray(value) ? `[${entries.length}]` : `{${entries.length}}`}
        </span>
      </button>
      {open && (
        <div className="ml-3 border-l border-theme-border pl-3 dark:border-themedark-border">
          {entries.map(([key, child]) => (
            <Node key={key} label={key} value={child} />
          ))}
        </div>
      )}
    </div>
  );
};

const JsonViewer = ({ data, className = '' }) => (
  <div className={`font-mono text-sm text-theme-bodycolor dark:text-themedark-bodycolor ${className}`}>
    {isBranch(data) ? (
      toEntries(data).map(([key, value]) => <Node key={key} label={key} value={value} defaultOpen />)
    ) : (
      <Primitive value={data} />
    )}
  </div>
);

export default JsonViewer;
